import * as THREE from 'three';

/**
 * Scene capture bridge. Scene3D registers its THREE objects here on mount so
 * the export dialog can render an image at an arbitrary camera pose without
 * standing up a parallel scene.
 *
 * Only valid while the 3D view is mounted — in practice the ExportDialog
 * should enforce that by switching to 3D view before offering capture.
 */

interface SceneRefs {
  gl: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
}

let current: SceneRefs | null = null;
let savedCameraState: {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  target: THREE.Vector3 | null;
} | null = null;

export function registerSceneRefs(refs: SceneRefs): void {
  current = refs;
}

export function unregisterSceneRefs(refs: SceneRefs): void {
  if (current && current.gl === refs.gl) {
    current = null;
  }
}

export function hasScene(): boolean {
  return current !== null;
}

export interface SceneAngle {
  /** Horizontal rotation around the scene center, radians (0 = +Z axis). */
  yaw: number;
  /** Elevation above the ground, radians (0 = horizon, PI/2 = top-down). */
  pitch: number;
  /** Distance from the camera to the look-at target, meters. */
  distance: number;
}

/**
 * Apply an angle to the live camera and render one frame. Returns the bounds
 * used for the look-at target so the caller can chain renders.
 */
export function applyAngle(angle: SceneAngle, sceneSize: { w: number; h: number }): void {
  if (!current) return;
  const { camera, gl, scene } = current;
  const { yaw, pitch, distance } = angle;

  const cx = sceneSize.w / 2;
  const cy = 0.5; // a touch above ground so we frame the shooters / walls
  const cz = sceneSize.h / 2;

  const x = cx + distance * Math.cos(pitch) * Math.sin(yaw);
  const y = cy + distance * Math.sin(pitch);
  const z = cz + distance * Math.cos(pitch) * Math.cos(yaw);

  camera.position.set(x, y, z);
  camera.lookAt(cx, cy, cz);
  camera.updateProjectionMatrix();
  gl.render(scene, camera);
}

/**
 * Save the current camera transform so {@link restoreCamera} can put it back
 * after an export. Call before mutating the camera via {@link applyAngle}.
 */
export function saveCamera(): void {
  if (!current) return;
  savedCameraState = {
    position: current.camera.position.clone(),
    quaternion: current.camera.quaternion.clone(),
    target: null,
  };
}

export function restoreCamera(): void {
  if (!current || !savedCameraState) return;
  current.camera.position.copy(savedCameraState.position);
  current.camera.quaternion.copy(savedCameraState.quaternion);
  current.camera.updateProjectionMatrix();
  current.gl.render(current.scene, current.camera);
  savedCameraState = null;
}

/**
 * Render at the given angle and return a PNG Blob. Throws if the scene has
 * not been registered (i.e. the 3D view is not mounted).
 */
export async function capture3DScene(
  angle: SceneAngle,
  sceneSize: { w: number; h: number },
): Promise<Blob> {
  if (!current) {
    throw new Error('3D scene is not mounted — switch to the 3D view first');
  }
  applyAngle(angle, sceneSize);

  return new Promise((resolve, reject) => {
    current!.gl.domElement.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to capture 3D scene'));
      },
      'image/png',
      1,
    );
  });
}

/**
 * Render at the given angle and return a data URL (used for live previews).
 */
export function capture3DDataUrl(
  angle: SceneAngle,
  sceneSize: { w: number; h: number },
): string | null {
  if (!current) return null;
  applyAngle(angle, sceneSize);
  return current.gl.domElement.toDataURL('image/png');
}
